import {
  Button,
  Card,
  Empty,
  Input,
  Menu,
  message,
  Popconfirm,
  Select,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import dayjs from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { queryErrorMessage } from '../CashTransactions/types';
import ParticipantDrawer from './ParticipantDrawer';
import { ExamNumberCell, RoomCell } from './SeatCells';
import {
  type CompetitionOptions,
  competitionLabel,
  downloadCsv,
  type Participant,
  participantName,
  type RoomOption,
  type SeatAllocation,
  scheduleLabel,
} from './types';

const { Title, Text } = Typography;

const STORAGE_KEY = 'competition_participants.competition_id';

// Column sorting: Mongolian alphabet, numbers by value ("2" before "10"),
// empty values last.
const byText = (a: string | null | undefined, b: string | null | undefined) => {
  if (!a || !b) return a ? -1 : b ? 1 : 0;
  return a.localeCompare(b, 'mn', { numeric: true });
};

type SeatChange = { exam_room_id?: number | null; exam_number?: string | null };

// "Оролцогчид": one Competition's Participants (CONTEXT.md), filtered by
// class year and exam room, exported, corrected by hand, and given Exam rooms
// and numbers — in the table cells, or one room for many selected rows.
export default function CompetitionParticipants() {
  const { level, runQuery } = useScreen();
  const canEdit = level === 'edit';

  const [options, setOptions] = useState<CompetitionOptions>({ competitions: [], rooms: [] });
  const [competitionId, setCompetitionId] = useState<number | null>(() => {
    try {
      return Number(localStorage.getItem(STORAGE_KEY)) || null;
    } catch {
      return null;
    }
  });
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(false);
  const [classYearId, setClassYearId] = useState<number | null>(null);
  const [roomId, setRoomId] = useState<number | 'none' | null>(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Participant | 'new' | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkRoomId, setBulkRoomId] = useState<number | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => {
    runQuery<CompetitionOptions>('competition_options')
      .then(([row]) => {
        if (!row) return;
        setOptions(row);
        setCompetitionId((current) =>
          current && row.competitions.some((c) => c.id === current)
            ? current
            : (row.competitions[0]?.id ?? null),
        );
      })
      .catch((e) => message.error(queryErrorMessage(e)));
  }, [runQuery]);

  const competition = options.competitions.find((c) => c.id === competitionId) ?? null;
  const isOlympiad = competition?.type === 'OLYMPIAD';
  const canSeat = canEdit && isOlympiad;

  const load = useCallback(() => {
    if (!competitionId) return;
    setLoading(true);
    runQuery<Participant>('competition_participants', { competitionId })
      .then(setParticipants)
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [runQuery, competitionId]);

  useEffect(load, [load]);

  const selectCompetition = (id: number) => {
    setCompetitionId(id);
    setClassYearId(null);
    setRoomId(null);
    setSelectedIds([]);
    setParticipants([]);
    try {
      localStorage.setItem(STORAGE_KEY, String(id));
    } catch {
      // Remembering the choice is a convenience only.
    }
  };

  // Rows in the chosen class year; the room filter and search narrow further.
  const inClass = useMemo(
    () => participants.filter((p) => !classYearId || p.competition_class_year_id === classYearId),
    [participants, classYearId],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (
      inClass
        .filter((p) => (roomId === 'none' ? !p.exam_room_id : !roomId || p.exam_room_id === roomId))
        .filter(
          (p) =>
            !term ||
            [
              p.submission_key,
              p.last_name,
              p.first_name,
              ...p.mobile_no,
              p.school_name,
              p.exam_number,
            ]
              .filter(Boolean)
              .some((v) => String(v).toLowerCase().includes(term)),
        )
        // Default order: registration date, oldest first; undated rows last.
        .sort((a, b) => byText(a.submitted_at, b.submitted_at) || a.id - b.id)
    );
  }, [inClass, roomId, search]);

  const classUnroomed = inClass.filter((p) => !p.exam_room_id).length;
  const classUnnumbered = inClass.filter((p) => !p.exam_number).length;

  // Left panel: every Competition class year with its participant count, and
  // for an Olympiad how many still have no room.
  const classStats = useMemo(() => {
    const stats = new Map<number, { total: number; unroomed: number }>();
    for (const p of participants) {
      const s = stats.get(p.competition_class_year_id) ?? { total: 0, unroomed: 0 };
      s.total += 1;
      if (!p.exam_room_id) s.unroomed += 1;
      stats.set(p.competition_class_year_id, s);
    }
    return stats;
  }, [participants]);

  // How many Participants each room holds, shown wherever a room is picked.
  const roomCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const p of participants) {
      if (p.exam_room_id) counts.set(p.exam_room_id, (counts.get(p.exam_room_id) ?? 0) + 1);
    }
    return counts;
  }, [participants]);

  const roomOptions = options.rooms.map((r) => ({
    value: r.id,
    label: `${r.label} (${roomCounts.get(r.id) ?? 0})`,
  }));

  const classItem = (key: string, label: string, total: number, unroomed: number) => ({
    key,
    style: { height: 'auto', lineHeight: 1.4, paddingBlock: 8 },
    label: (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span>{label}</span>
          <Text type="secondary">{total}</Text>
        </div>
        {isOlympiad && total > 0 && (
          <Text type={unroomed ? 'warning' : 'success'} style={{ fontSize: 12 }}>
            {unroomed ? `Өрөөгүй ${unroomed}` : 'Бүгд өрөөтэй'}
          </Text>
        )}
      </div>
    ),
  });

  // Saves seats and patches the rows in place: no reload, the table keeps its
  // scroll position and page.
  const saveSeats = async (items: ({ id: number } & SeatChange)[]) => {
    if (!competitionId) return false;
    try {
      await runQuery('competition_participant_seats_save', { competitionId, items });
      const byId = new Map(items.map((i) => [i.id, i]));
      setParticipants((rows) =>
        rows.map((p) => {
          const change = byId.get(p.id);
          if (!change) return p;
          const next = { ...p };
          if (change.exam_room_id !== undefined) {
            next.exam_room_id = change.exam_room_id;
            next.exam_room_number =
              options.rooms.find((r) => r.id === change.exam_room_id)?.number ?? null;
          }
          if (change.exam_number !== undefined) next.exam_number = change.exam_number;
          return next;
        }),
      );
      return true;
    } catch (e) {
      message.error(queryErrorMessage(e));
      return false;
    }
  };

  // Seat allocation (CONTEXT.md): the server seats these Participants in the
  // room, newest registration first, on its free numbers up to the class
  // year's Room capacity, and leaves the rest without a room or number.
  const allocate = async (roomId: number, participantIds: number[]) => {
    if (!competitionId) return false;
    try {
      const [row] = await runQuery<SeatAllocation>('competition_seats_allocate', {
        competitionId,
        roomId,
        participantIds,
      });
      const unseated = row?.unseated ?? 0;
      const text = `${row?.seated ?? 0} оролцогчид суудал хуваарилалаа.`;
      if (unseated) {
        message.warning(
          `${text} Багтаамж хүрэлцээгүй тул ${unseated} оролцогчид суудал олгосонгүй.`,
        );
      } else {
        message.success(text);
      }
      load();
      return true;
    } catch (e) {
      message.error(queryErrorMessage(e));
      return false;
    }
  };

  // "Өрөө оноох" seats the selected rows; "Өрөө арилгах" clears their room
  // and number.
  const assignBulkRoom = async (room: number | null) => {
    setBulkSaving(true);
    const ok = room
      ? await allocate(room, selectedIds)
      : await saveSeats(selectedIds.map((id) => ({ id, exam_room_id: null, exam_number: null })));
    setBulkSaving(false);
    if (ok) {
      if (!room) message.success(`${selectedIds.length} оролцогчийн өрөөг арилгалаа.`);
      setSelectedIds([]);
      setBulkRoomId(null);
    }
  };

  // Enter in a number cell moves to the number of the row below, in the
  // order shown (a column sort included).
  const focusNextNumber = (id: number) => {
    const inputs = [...document.querySelectorAll<HTMLInputElement>('input[data-exam-number]')];
    const index = inputs.findIndex((el) => el.dataset.examNumber === String(id));
    inputs[index + 1]?.focus();
  };

  const capacityFor = (room: RoomOption, classYear: number | null) =>
    room.capacities.find((c) => c.class_year_id === classYear)?.capacity ?? null;
  // The selected rows' class year, when they share one: its Room capacity
  // shows in the "Өрөө оноох" picker.
  const selectedClassYearId = (() => {
    const ids = new Set(
      participants
        .filter((p) => selectedIds.includes(p.id))
        .map((p) => p.competition_class_year_id),
    );
    return ids.size === 1 ? [...ids][0] : null;
  })();
  const bulkRoomOptions = options.rooms.map((r) => {
    const capacity = capacityFor(r, selectedClassYearId);
    const count = roomCounts.get(r.id) ?? 0;
    return {
      value: r.id,
      label: capacity ? `${r.label} (${count}/${capacity})` : `${r.label} — багтаамжгүй`,
      disabled: !capacity,
    };
  });

  const exportCsv = () => {
    if (!competition) return;
    downloadCsv(`${competition.name}-oroltsogchid.csv`, [
      [
        'Бүртгэлийн түлхүүр',
        'Бүртгүүлсэн огноо',
        'Овог',
        'Нэр',
        'Тэмцээний анги',
        'Шалгалтын цаг',
        'Сургууль',
        'Суралцдаг анги',
        'Утас',
        'И-мэйл',
        'Оролцож буй удаа',
        ...(isOlympiad ? ['Шалгалтын өрөө', 'Шалгалтын дугаар'] : []),
        'Төлбөрийн баримт',
      ],
      ...filtered.map((p) => [
        p.submission_key,
        p.submitted_at ? dayjs(p.submitted_at).format('YYYY.MM.DD HH:mm') : '',
        p.last_name,
        p.first_name,
        p.class_year_name,
        scheduleLabel(p),
        p.school_name,
        p.participant_class_year,
        p.mobile_no.join(', '),
        p.email_address,
        p.participation_no,
        ...(isOlympiad ? [p.exam_room_number, p.exam_number] : []),
        p.payment_proof_url,
      ]),
    ]);
  };

  const columns: ColumnsType<Participant> = [
    {
      title: 'Овог нэр',
      key: 'name',
      fixed: 'left',
      width: 200,
      sorter: (a, b) => byText(participantName(a), participantName(b)),
      render: (_: unknown, p) => <Text strong>{participantName(p)}</Text>,
    },
    // The panel already says which class is shown.
    ...(classYearId
      ? []
      : [
          {
            title: 'Тэмцээний анги',
            dataIndex: 'class_year_name',
            width: 110,
            sorter: (a: Participant, b: Participant) =>
              (a.class_number ?? 99) - (b.class_number ?? 99),
          },
        ]),
    {
      title: 'Сургууль',
      dataIndex: 'school_name',
      width: 200,
      sorter: (a, b) => byText(a.school_name, b.school_name),
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Суралцдаг анги',
      dataIndex: 'participant_class_year',
      width: 90,
      align: 'center',
      sorter: (a, b) => byText(a.participant_class_year, b.participant_class_year),
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Утас',
      dataIndex: 'mobile_no',
      width: 110,
      sorter: (a, b) => byText(a.mobile_no[0], b.mobile_no[0]),
      // One number per line when a form gave several.
      render: (v: string[]) =>
        v.length ? <span style={{ whiteSpace: 'pre-line' }}>{v.join('\n')}</span> : '—',
    },
    {
      title: 'Бүртгүүлсэн',
      dataIndex: 'submitted_at',
      width: 140,
      sorter: (a, b) => byText(a.submitted_at, b.submitted_at),
      render: (v: string | null) => (v ? dayjs(v).format('YYYY.MM.DD HH:mm') : '—'),
    },
    {
      // A forms.mn response id is a long UUID: its start is enough to tell
      // rows apart; the copy button gives the whole key.
      title: 'Түлхүүр',
      dataIndex: 'submission_key',
      width: 130,
      sorter: (a, b) => byText(a.submission_key, b.submission_key),
      render: (v: string) => (
        // biome-ignore lint/a11y/noStaticElementInteractions: only keeps the copy click off the row
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <Text type="secondary" copyable={{ text: v }} style={{ fontSize: 12 }}>
            {v.length > 10 ? `${v.slice(0, 8)}…` : v}
          </Text>
        </span>
      ),
    },
    ...(isOlympiad
      ? [
          {
            title: 'Өрөө',
            key: 'room',
            fixed: 'right' as const,
            width: 150,
            sorter: (a: Participant, b: Participant) =>
              byText(a.exam_room_number, b.exam_room_number),
            render: (_: unknown, p: Participant) =>
              canEdit ? (
                <RoomCell
                  value={p.exam_room_id}
                  rooms={options.rooms}
                  disabled={false}
                  onSave={(exam_room_id) => saveSeats([{ id: p.id, exam_room_id }])}
                />
              ) : (
                (p.exam_room_number ?? '—')
              ),
          },
          {
            title: 'Дугаар',
            key: 'exam_number',
            fixed: 'right' as const,
            width: 110,
            sorter: (a: Participant, b: Participant) => byText(a.exam_number, b.exam_number),
            render: (_: unknown, p: Participant) =>
              canEdit ? (
                <ExamNumberCell
                  participantId={p.id}
                  value={p.exam_number}
                  disabled={false}
                  onSave={(exam_number) => saveSeats([{ id: p.id, exam_number }])}
                  onNext={() => focusNextNumber(p.id)}
                />
              ) : (
                (p.exam_number ?? '—')
              ),
          },
        ]
      : []),
  ];

  const selectedClass = competition?.class_years.find((cy) => cy.id === classYearId);

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Оролцогчид
          </Title>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Button onClick={exportCsv} disabled={!filtered.length}>
              CSV татах
            </Button>
            {canEdit && competition && (
              <Button type="primary" onClick={() => setEditing('new')}>
                Оролцогч нэмэх
              </Button>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <Select
            style={{ minWidth: 280, flex: '1 1 280px', maxWidth: 640 }}
            placeholder="Тэмцээн сонгох"
            value={competitionId ?? undefined}
            onChange={selectCompetition}
            showSearch
            optionFilterProp="label"
            options={options.competitions.map((c) => ({
              value: c.id,
              label: competitionLabel(c),
            }))}
          />
          {isOlympiad && (
            <Select
              style={{ minWidth: 180 }}
              placeholder="Бүх өрөө"
              allowClear
              showSearch
              optionFilterProp="label"
              value={roomId ?? undefined}
              onChange={(v) => setRoomId(v ?? null)}
              options={[{ value: 'none', label: 'Өрөөгүй' }, ...roomOptions]}
            />
          )}
          <Input.Search
            style={{ maxWidth: 280 }}
            placeholder="Нэр, утас, түлхүүр, дугаар..."
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {competition ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <Menu
              mode="inline"
              style={{ width: 200, flexShrink: 0, borderInlineEnd: 'none' }}
              selectedKeys={[classYearId ? String(classYearId) : 'all']}
              onClick={({ key }) => {
                setClassYearId(key === 'all' ? null : Number(key));
                setSelectedIds([]);
              }}
              items={[
                classItem(
                  'all',
                  'Бүх анги',
                  participants.length,
                  participants.filter((p) => !p.exam_room_id).length,
                ),
                ...[...competition.class_years]
                  .sort((a, b) => a.class_number - b.class_number)
                  .map((cy) => {
                    const s = classStats.get(cy.id) ?? { total: 0, unroomed: 0 };
                    return classItem(String(cy.id), cy.name, s.total, s.unroomed);
                  }),
              ]}
            />
            <div style={{ flex: '1 1 480px', minWidth: 0 }}>
              {/* What this view holds; the counts double as quick filters. */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <Text strong>{selectedClass?.name ?? 'Бүх анги'}</Text>
                <Text type="secondary">{inClass.length} оролцогч</Text>
                {isOlympiad && inClass.length > 0 && (
                  <>
                    <Tag
                      color={classUnroomed ? 'orange' : 'green'}
                      style={{ cursor: classUnroomed ? 'pointer' : undefined, marginInlineEnd: 0 }}
                      onClick={() => classUnroomed && setRoomId(roomId === 'none' ? null : 'none')}
                    >
                      {classUnroomed ? `Өрөөгүй ${classUnroomed}` : 'Бүгд өрөөтэй'}
                    </Tag>
                    <Tag color={classUnnumbered ? 'orange' : 'green'}>
                      {classUnnumbered ? `Дугааргүй ${classUnnumbered}` : 'Бүгд дугаартай'}
                    </Tag>
                  </>
                )}
              </div>

              {canSeat && selectedIds.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    marginBottom: 12,
                    borderRadius: 8,
                    background: 'rgba(22, 119, 255, 0.06)',
                    border: '1px solid rgba(22, 119, 255, 0.2)',
                  }}
                >
                  <Text strong>{selectedIds.length} сонгосон</Text>
                  <Select
                    style={{ minWidth: 200 }}
                    placeholder={selectedClassYearId ? 'Өрөө сонгох' : 'Нэг ангийг сонгоно уу'}
                    showSearch
                    optionFilterProp="label"
                    popupMatchSelectWidth={false}
                    disabled={!selectedClassYearId}
                    value={bulkRoomId ?? undefined}
                    onChange={setBulkRoomId}
                    options={bulkRoomOptions}
                  />
                  <Button
                    type="primary"
                    disabled={!bulkRoomId || !selectedClassYearId}
                    loading={bulkSaving}
                    onClick={() => assignBulkRoom(bulkRoomId)}
                  >
                    Өрөө оноох
                  </Button>
                  <Popconfirm
                    title={`${selectedIds.length} оролцогчийн өрөө, дугаарыг арилгах уу?`}
                    okText="Арилгах"
                    cancelText="Болих"
                    onConfirm={() => assignBulkRoom(null)}
                  >
                    <Button disabled={bulkSaving}>Өрөө арилгах</Button>
                  </Popconfirm>
                  <Button type="link" onClick={() => setSelectedIds([])}>
                    Цуцлах
                  </Button>
                </div>
              )}

              <Table
                rowKey="id"
                size="small"
                columns={columns}
                dataSource={filtered}
                loading={loading}
                scroll={{ x: 'max-content' }}
                rowSelection={
                  canSeat
                    ? {
                        selectedRowKeys: selectedIds,
                        onChange: (keys) => setSelectedIds(keys as number[]),
                        preserveSelectedRowKeys: false,
                      }
                    : undefined
                }
                onRow={(p) => ({ onClick: () => setEditing(p), style: { cursor: 'pointer' } })}
                pagination={{
                  defaultPageSize: 50,
                  showSizeChanger: true,
                  showTotal: (total) => `Нийт ${total}`,
                }}
              />
            </div>
          </div>
        ) : (
          <Empty description="Тэмцээн сонгоно уу." />
        )}
      </Card>

      {editing && competition && (
        <ParticipantDrawer
          competition={competition}
          rooms={options.rooms}
          participant={editing === 'new' ? null : editing}
          canEdit={canEdit}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </LayoutWithMenu>
  );
}
